import React from "react";
import { StyleSheet, View, Image } from "react-native";
import { useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import { ActivityLogModel } from "common247";
import { ThemedText } from "@/components/ThemedText";
import { ThemedButton } from "@/components/ThemedButton";
import { EntityIcon } from "@/components/ui/EntityIcon";
import { useThemeColor } from "@/hooks/useThemeColor";
import { ThemedEntityLink, getEntityRoute } from "@/components/ThemedEntityLink";
import { ThemedChip } from "@/components/ThemedChip";
import { ThemedCompactCard } from "@/components/cards/ThemedCompactCard";
import { ButtonPaddings } from "@/constants/themes/components";
import { Spacing } from "@/constants/ThemeColours";
import { ThemedListViewMode } from "@/components/ThemedList";
import {
  getActionBadgeConfig,
  formatTimeAgo,
  extractSecondaryEntities,
  type ISecondaryEntity,
} from "./activity-card-helpers";
import { ActivityCompactRow, ActivitySmallCard } from "./ActivityDensityRows";

export { getActionBadgeConfig, formatTimeAgo, extractSecondaryEntities, type ISecondaryEntity };

export interface IActivityCardProps {
  activity: ActivityLogModel;
  viewMode?: ThemedListViewMode;
  isHovered?: boolean;
  isTimeline?: boolean;
  onPress?: () => void;
}

/**
 * ActivityCard renders a single activity log item adapting across list view modes.
 * Aligns with Feature 380 ("Universal Entity Linking & Navigation Across Screens")
 * and Feature 454 (Chip cards for Contacts/Groups/Shares, Compact cards for Memories/Media).
 */
export function ActivityCard({
  activity,
  viewMode = "full",
  isHovered = false,
  isTimeline = true,
  onPress,
}: IActivityCardProps) {
  const router = useRouter();

  const cardBg = useThemeColor({}, "card");
  const borderColor = useThemeColor({}, "border");
  const hoverBorderColor = useThemeColor({}, "buttonHoverBorder");
  const textMuted = useThemeColor({}, "textMuted");
  const tintColor = useThemeColor({}, "tint");
  const activeBorder = isHovered ? hoverBorderColor : borderColor;

  const actionConfig = getActionBadgeConfig(activity.action);
  const timeAgoStr = formatTimeAgo(activity.createdAt);
  const actorName = activity.isOwnActivity ? "You" : activity.actorDisplayName || "User";

  const isNavigableEntity = Boolean(
    activity.targetId &&
    activity.targetId !== "unknown" &&
    activity.targetId !== "auth" &&
    activity.targetType !== "auth"
  );

  const handleNavigate = () => {
    if (activity.targetRoute) {
      router.push(activity.targetRoute as any);
    } else if (isNavigableEntity) {
      router.push(getEntityRoute(activity.targetType, activity.targetId) as any);
    } else if (onPress) {
      onPress();
    }
  };

  const secondaryLinks = extractSecondaryEntities(activity);

  if (viewMode === "compact") {
    return (
      <ActivityCompactRow
        activity={activity}
        actionConfig={actionConfig}
        actorName={actorName}
        timeAgoStr={timeAgoStr}
        isNavigableEntity={isNavigableEntity}
        cardBg={cardBg}
        activeBorder={activeBorder}
        textMuted={textMuted}
        tintColor={tintColor}
        onNavigate={activity.targetRoute ? handleNavigate : onPress}
      />
    );
  }

  if (viewMode === "small") {
    return (
      <ActivitySmallCard
        activity={activity}
        actionConfig={actionConfig}
        actorName={actorName}
        timeAgoStr={timeAgoStr}
        isNavigableEntity={isNavigableEntity}
        cardBg={cardBg}
        activeBorder={activeBorder}
        textMuted={textMuted}
        tintColor={tintColor}
        onNavigate={activity.targetRoute ? handleNavigate : onPress}
      />
    );
  }

  // Detailed / Timeline View
  const contentCard = (
    <View
      style={[
        styles.card,
        isTimeline && styles.timelineCard,
        { backgroundColor: cardBg, borderColor: activeBorder },
      ]}
    >
      <View style={styles.cardHeader}>
        <View style={styles.actorInfo}>
          {activity.actorAvatarUrl ? (
            <Image source={{ uri: activity.actorAvatarUrl }} style={styles.avatar} />
          ) : (
            <View style={[styles.avatarPlaceholder, { backgroundColor: tintColor }]}>
              <ThemedText style={styles.avatarLetter}>
                {actorName.charAt(0).toUpperCase()}
              </ThemedText>
            </View>
          )}
          <View>
            <ThemedText style={styles.actorName}>
              {actorName}
              {activity.actorEmail && activity.actorEmail !== actorName ? (
                <ThemedText style={[styles.actorEmail, { color: textMuted }]}>
                  {" "}• {activity.actorEmail}
                </ThemedText>
              ) : null}
            </ThemedText>
            <ThemedText style={[styles.timeLabel, { color: textMuted }]}>
              {timeAgoStr}
              {activity.createdAt
                ? ` (${new Date(activity.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })})`
                : ""}
            </ThemedText>
          </View>
        </View>

        <View style={styles.badgesRow}>
          <View style={[styles.badge, { backgroundColor: actionConfig.bg }]}>
            <Ionicons name={actionConfig.icon} size={12} color={actionConfig.text} style={{ marginRight: 4 }} />
            <ThemedText style={[styles.badgeText, { color: actionConfig.text }]}>
              {actionConfig.label}
            </ThemedText>
          </View>

          {isNavigableEntity ? (
            <ThemedEntityLink
              type={activity.targetType}
              id={activity.targetId}
              displayName={activity.targetType}
              variant="badge"
              showIcon={true}
              style={[styles.badge, styles.targetTypeBadge]}
              textStyle={[styles.badgeText, { color: tintColor, textTransform: "capitalize" }]}
              onPress={activity.targetRoute ? handleNavigate : undefined}
            />
          ) : (
            <View style={[styles.badge, styles.targetTypeBadge]}>
              <EntityIcon type={activity.targetType} size={12} color={tintColor} style={{ marginRight: 4 }} />
              <ThemedText style={[styles.badgeText, { color: tintColor, textTransform: "capitalize" }]}>
                {activity.targetType}
              </ThemedText>
            </View>
          )}
        </View>
      </View>

      <View style={styles.cardBody}>
        <ThemedText style={styles.description}>
          {activity.description}
        </ThemedText>

        {/* Primary Target Entity Card */}
        {isNavigableEntity && (
          <View style={styles.targetEntityContainer}>
            {activity.targetType === "memory" || activity.targetType === "media" ? (
              <ThemedCompactCard
                entityType={activity.targetType as any}
                id={activity.targetId}
                title={activity.targetTitle || activity.description}
                thumbnailUrl={activity.details?.thumbnailUrl || activity.details?.downloadUrl || activity.details?.url}
                date={activity.details?.date || activity.details?.memoryDate}
                onPress={activity.targetRoute ? handleNavigate : undefined}
                style={styles.targetCompactCard}
                testID={`activity-target-${activity.targetType}-${activity.targetId}`}
              />
            ) : activity.targetType === "contact" || activity.targetType === "group" || activity.targetType === "share" ? (
              <ThemedChip
                entityType={activity.targetType}
                id={activity.targetId}
                label={activity.targetTitle || `${activity.targetType} #${activity.targetId}`}
                secondaryLabel={activity.details?.recipientName || activity.details?.shareRecipient}
                thumbnailUrl={activity.details?.avatarUrl || activity.details?.thumbnailUrl}
                onPress={activity.targetRoute ? handleNavigate : undefined}
                style={styles.targetChip}
                testID={`activity-target-${activity.targetType}-${activity.targetId}`}
              />
            ) : (
              <ThemedEntityLink
                type={activity.targetType}
                id={activity.targetId}
                displayName={activity.targetTitle || activity.description}
                variant="title"
                showIcon={true}
                showOpenIcon={true}
                onPress={activity.targetRoute ? handleNavigate : undefined}
                textStyle={styles.targetTitle}
                numberOfLines={2}
              />
            )}
          </View>
        )}

        {!isNavigableEntity && activity.targetTitle ? (
          <ThemedText style={styles.targetTitle} numberOfLines={2}>
            {activity.targetTitle}
          </ThemedText>
        ) : null}

        {/* Secondary / Related Entities */}
        {secondaryLinks.length > 0 && (
          <View style={styles.secondaryLinksContainer}>
            <ThemedText style={[styles.secondaryLinksLabel, { color: textMuted }]}>
              Related:
            </ThemedText>
            <View style={styles.secondaryLinksWrap}>
              {secondaryLinks.map((sec, idx) => {
                if (sec.type === "memory" || sec.type === "media") {
                  return (
                    <ThemedCompactCard
                      key={`${sec.type}_${sec.id}_${idx}`}
                      entityType={sec.type as any}
                      id={sec.id}
                      title={sec.label}
                      thumbnailUrl={sec.thumbnailUrl}
                      date={sec.date}
                      style={styles.secondaryCompactCard}
                      testID={`activity-related-${sec.type}-${sec.id}`}
                    />
                  );
                }
                if (sec.type === "contact" || sec.type === "group" || sec.type === "share") {
                  return (
                    <ThemedChip
                      key={`${sec.type}_${sec.id}_${idx}`}
                      entityType={sec.type}
                      id={sec.id}
                      label={sec.label}
                      secondaryLabel={sec.recipientName}
                      thumbnailUrl={sec.thumbnailUrl}
                      style={styles.secondaryChip}
                      testID={`activity-related-${sec.type}-${sec.id}`}
                    />
                  );
                }
                return (
                  <ThemedEntityLink
                    key={`${sec.type}_${sec.id}_${idx}`}
                    type={sec.type}
                    id={sec.id}
                    displayName={sec.label}
                    variant="badge"
                    showIcon={true}
                    showOpenIcon={true}
                    style={styles.secondaryBadge}
                  />
                );
              })}
            </View>
          </View>
        )}
      </View>

      {activity.targetRoute && (
        <View style={styles.cardFooter}>
          <ThemedButton
            title={`View ${activity.targetType}`}
            onPress={handleNavigate}
            variant="secondary"
            icon={<Ionicons name="arrow-forward" size={14} color={tintColor} />}
            iconPosition="right"
          />
        </View>
      )}
    </View>
  );

  if (isTimeline) {
    return (
      <View style={styles.timelineRow}>
        <View style={styles.timelineSpine}>
          <View style={[styles.timelineLine, { backgroundColor: borderColor }]} />
          <View
            style={[
              styles.timelineNode,
              {
                backgroundColor: actionConfig.bg,
                borderColor: actionConfig.text,
              },
            ]}
          >
            <Ionicons name={actionConfig.icon} size={14} color={actionConfig.text} />
          </View>
        </View>
        <View style={styles.timelineContentContainer}>
          {contentCard}
        </View>
      </View>
    );
  }

  return contentCard;
}

const styles = StyleSheet.create({
  timelineRow: {
    flexDirection: "row",
    alignItems: "stretch",
    width: "100%",
    position: "relative",
  },
  timelineSpine: {
    width: 36,
    alignItems: "center",
    position: "relative",
  },
  timelineLine: {
    position: "absolute",
    top: 0,
    bottom: -Spacing.sm,
    width: 2,
    left: 17,
    zIndex: 1,
  },
  timelineNode: {
    width: 28,
    height: 28,
    borderRadius: 14,
    borderWidth: 2,
    alignItems: "center",
    justifyContent: "center",
    marginTop: 10,
    zIndex: 2,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.1,
    shadowRadius: 2,
    elevation: 2,
  },
  timelineContentContainer: {
    flex: 1,
    paddingLeft: Spacing.xs,
    width: "100%",
  },
  timelineCard: {
    borderRadius: 8,
    marginBottom: Spacing.sm,
    width: "100%",
  },
  card: {
    borderWidth: 1,
    padding: Spacing.md,
    marginBottom: Spacing.sm,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 1 },
    shadowOpacity: 0.05,
    shadowRadius: 2,
    elevation: 1,
    width: "100%",
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: Spacing.sm,
    flexWrap: "wrap",
    gap: 8,
  },
  actorInfo: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  avatar: {
    width: 32,
    height: 32,
    borderRadius: 16,
  },
  avatarPlaceholder: {
    width: 32,
    height: 32,
    borderRadius: 16,
    alignItems: "center",
    justifyContent: "center",
  },
  avatarLetter: {
    color: "#ffffff",
    fontWeight: "700",
    fontSize: 14,
  },
  actorName: {
    fontSize: 14,
    fontWeight: "600",
  },
  actorEmail: {
    fontSize: 12,
    fontWeight: "400",
  },
  timeLabel: {
    fontSize: 11,
    marginTop: 1,
  },
  badgesRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  badge: {
    flexDirection: "row",
    alignItems: "center",
    paddingHorizontal: 8,
    paddingVertical: 3,
  },
  targetTypeBadge: {
    backgroundColor: "rgba(0,0,0,0.04)",
  },
  badgeText: {
    fontSize: 11,
    fontWeight: "600",
  },
  cardBody: {
    marginTop: 2,
  },
  targetTitle: {
    fontSize: 15,
    fontWeight: "700",
    marginBottom: 4,
  },
  description: {
    fontSize: 13,
    lineHeight: 18,
    opacity: 0.9,
  },
  cardFooter: {
    flexDirection: "row",
    justifyContent: "flex-end",
    marginTop: Spacing.sm,
    paddingTop: Spacing.xs,
  },
  secondaryLinksContainer: {
    flexDirection: "row",
    alignItems: "center",
    marginTop: Spacing.sm,
    flexWrap: "wrap",
    gap: 6,
  },
  secondaryLinksLabel: {
    fontSize: 12,
    fontWeight: "600",
  },
  secondaryLinksWrap: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 6,
  },
  secondaryBadge: {
    paddingHorizontal: ButtonPaddings.action.paddingHorizontal,
    paddingVertical: ButtonPaddings.action.paddingVertical,
  },
  targetEntityContainer: {
    marginTop: Spacing.xs,
    marginBottom: 4,
  },
  targetCompactCard: {
    maxWidth: 360,
  },
  targetChip: {
    alignSelf: "flex-start",
  },
  secondaryCompactCard: {
    maxWidth: 280,
  },
  secondaryChip: {
    alignSelf: "flex-start",
  },
});

export default ActivityCard;
